import React, { useEffect, useState, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Truck } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { formatApiError } from '@/utils/apiError';

/**
 * Full-page "Add a delivery" workflow. Any staff member can record a delivery.
 * Backend writes per-line movements + emails orders@oxresin.co.uk summarising
 * what was booked in.
 */
export default function StockDelivery() {
  const navigate = useNavigate();
  const { isStaff } = useContext(AuthContext);

  const [allProducts, setAllProducts] = useState([]);
  const [supplier, setSupplier] = useState('');
  const [reference, setReference] = useState('');
  const [selected, setSelected] = useState({});
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/stock/products`, { credentials: 'include' });
    if (!res.ok) { toast.error('Could not load products'); return; }
    setAllProducts(await res.json());
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
  }, [isStaff, navigate, load]);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const filtered = q
      ? allProducts.filter(p => p.name.toLowerCase().includes(q) || (p.category || '').toLowerCase().includes(q))
      : allProducts;
    const byCat = new Map();
    for (const p of filtered) {
      const c = p.category || 'Uncategorised';
      if (!byCat.has(c)) byCat.set(c, []);
      byCat.get(c).push(p);
    }
    return Array.from(byCat.entries());
  }, [allProducts, filter]);

  const selectedCount = Object.keys(selected).length;
  const toggle = (id) => setSelected(prev => {
    const next = { ...prev };
    if (id in next) delete next[id]; else next[id] = '1';
    return next;
  });
  const setQty = (id, v) => setSelected(prev => ({ ...prev, [id]: v }));

  const submit = async () => {
    const items = Object.entries(selected)
      .map(([product_id, q]) => ({ product_id, quantity: parseInt(q, 10) }))
      .filter(i => Number.isFinite(i.quantity) && i.quantity > 0);
    if (items.length === 0) { toast.error('Tick at least one product and enter a quantity.'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/deliveries`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ supplier: supplier || null, reference: reference || null, items }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data) || 'Failed');
      if (data.pending_approval) {
        toast.success('Delivery submitted to your manager for approval.', { description: 'You\u2019ll be notified by email once it\u2019s approved.' });
        navigate('/staff/stock/approvals');
        return;
      }
      const okLines = (data.items || []).filter(i => i.ok).length;
      toast.success(`Delivery booked in — ${okLines} line${okLines === 1 ? '' : 's'}. Notification sent to orders@oxresin.co.uk.`);
      navigate('/staff/stock');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="delivery-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><Truck className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Add a Delivery</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Book stock in as it arrives — orders@oxresin.co.uk is notified automatically.</p>
          </div>
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 py-5 space-y-4">
        <Card className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-sm font-semibold">Supplier (optional)</Label>
              <Input data-testid="delivery-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="e.g. AcmeChem" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Reference (optional)</Label>
              <Input data-testid="delivery-reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. DLV-2026-001" className="mt-1" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Filter</Label>
              <Input data-testid="delivery-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Narrow by product or category…" className="mt-1" />
            </div>
          </div>
        </Card>

        {grouped.length === 0 ? (
          <Card className="p-10 text-center text-gray-500 italic">No products match that filter.</Card>
        ) : grouped.map(([category, products]) => (
          <Card key={category} data-testid={`delivery-cat-${category}`}>
            <div className="px-3 py-2 text-[11px] uppercase tracking-wider font-bold text-gray-600 bg-gray-100 rounded-t-xl">{category}</div>
            <div className="divide-y divide-gray-100">
              {products.map(p => {
                const checked = p.id in selected;
                return (
                  <label
                    key={p.id}
                    data-testid={`delivery-product-row-${p.id}`}
                    className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${checked ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`delivery-check-${p.id}`}
                      checked={checked}
                      onChange={() => toggle(p.id)}
                      className="w-4 h-4 accent-blue-600 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-gray-900 truncate">{p.name}</div>
                      <div className="text-[11px] text-gray-500">{p.qty_on_hand} on hand</div>
                    </div>
                    {checked && (
                      <Input
                        data-testid={`delivery-qty-${p.id}`}
                        type="number" min="1"
                        value={selected[p.id]}
                        onChange={(e) => setQty(p.id, e.target.value)}
                        onClick={(e) => e.preventDefault()}
                        aria-label={`Quantity for ${p.name}`}
                        className="w-24 shrink-0 text-right"
                      />
                    )}
                  </label>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-[1500px] mx-auto px-4 py-3 flex items-center justify-end gap-2">
          <span className="text-sm text-gray-600 mr-auto" data-testid="delivery-selected-count">
            <strong>{selectedCount}</strong> product{selectedCount === 1 ? '' : 's'} selected
          </span>
          <Button variant="outline" onClick={() => navigate('/staff')} data-testid="delivery-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || selectedCount === 0} data-testid="delivery-submit" className="bg-[#1d4ed8] hover:bg-[#1e40af] text-white disabled:opacity-50">
            <Truck className="w-4 h-4 mr-1.5" /> {busy ? 'Saving…' : 'Book delivery in'}
          </Button>
        </div>
      </div>
    </div>
  );
}
